/**
 * computeMappingCleanup — Pure diff between two workflow schemas.
 *
 * sl-g3on.3.1: When the user switches the automation block to a different
 * workflow, its `inputMappings` / `outputMappings` may still reference
 * field IDs that no longer exist (or moved to a different type). Persisting
 * such stale mappings causes silent correctness bugs in OutputWriter and
 * confusing UX ("this column is mapped to a field that doesn't exist").
 *
 * This utility is intentionally a pure TypeScript function — no React, no
 * Y.js, no NestJS. The caller is expected to:
 *   1. Resolve the old schema (usually from the previously-cached workflow).
 *   2. Fetch the new schema via the existing workflow service.
 *   3. Call `computeMappingCleanup` with both.
 *   4. Commit the returned `cleanInputMappings` / `cleanOutputMappings` to
 *      the block props in one Y.js transaction (see sl-g3on.3.2).
 *
 * The algorithm never mutates its inputs; every returned array is a fresh
 * shallow copy.
 */

import type { InputMapping, OutputMapping } from '../services/automation';

// ─── Minimal schema shapes ──────────────────────────────────────────────────
//
// We re-declare the parts of WorkflowSchema we actually need so the function
// stays decoupled from the full WorkflowSchema type (which would drag in
// transitive WorkflowSchemaField / WorkflowSchemaOutputField references).
// The runtime contract is: the caller passes an object shaped like the
// fields below; any additional properties on the concrete schema type are
// ignored.

export interface MappingCleanupInputField {
  readonly id: string;
  readonly type?: string;
}

export interface MappingCleanupInputSchema {
  readonly fields: ReadonlyArray<MappingCleanupInputField>;
}

export interface MappingCleanupOutputField {
  readonly id: string;
  readonly type?: string;
}

export interface MappingCleanupOutputSchema {
  readonly fields: ReadonlyArray<MappingCleanupOutputField>;
}

// ─── Result shape ───────────────────────────────────────────────────────────

export type RemovalReason = 'field-missing' | 'type-mismatch';

export interface RemovedInputMapping {
  readonly mapping: InputMapping;
  readonly reason: RemovalReason;
}

export interface RemovedOutputMapping {
  readonly mapping: OutputMapping;
  readonly reason: RemovalReason;
}

export interface MappingCleanupResult {
  readonly cleanInputMappings: InputMapping[];
  readonly cleanOutputMappings: OutputMapping[];
  readonly removed: {
    readonly inputs: ReadonlyArray<RemovedInputMapping>;
    readonly outputs: ReadonlyArray<RemovedOutputMapping>;
  };
}

// ─── Core helpers (tested directly) ─────────────────────────────────────────

/** Build a fieldId → type map from a schema. Empty string when type is absent. */
export function buildTypeIndex(
  schema: MappingCleanupInputSchema | MappingCleanupOutputSchema
): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  for (const field of schema.fields) {
    map.set(field.id, field.type ?? '');
  }
  return map;
}

/**
 * Determine how a single input mapping should be handled against the new
 * schema. Exposed so UI layers can preview the reason individually.
 */
export function classifyInputMapping(
  mapping: InputMapping,
  oldIndex: ReadonlyMap<string, string> | null,
  newIndex: ReadonlyMap<string, string>
): 'keep' | RemovalReason {
  if (!newIndex.has(mapping.schemaFieldId)) {
    return 'field-missing';
  }
  if (!oldIndex) return 'keep';
  const oldType = oldIndex.get(mapping.schemaFieldId);
  const newType = newIndex.get(mapping.schemaFieldId);
  if (oldType && newType && oldType !== newType) {
    return 'type-mismatch';
  }
  return 'keep';
}

export function classifyOutputMapping(
  mapping: OutputMapping,
  oldIndex: ReadonlyMap<string, string> | null,
  newIndex: ReadonlyMap<string, string>
): 'keep' | RemovalReason {
  // Skip fixed-value mappings — they do not reference a source field.
  if (mapping.fixedValue !== undefined && mapping.fixedValue !== '') {
    return 'keep';
  }
  // A mapping without a responseField is not yet configured — leave it.
  if (!mapping.responseField) return 'keep';
  if (!newIndex.has(mapping.responseField)) {
    return 'field-missing';
  }
  if (!oldIndex) return 'keep';
  const oldType = oldIndex.get(mapping.responseField);
  const newType = newIndex.get(mapping.responseField);
  if (oldType && newType && oldType !== newType) {
    return 'type-mismatch';
  }
  return 'keep';
}

// ─── Public entry point ─────────────────────────────────────────────────────

/**
 * Diff two workflow schemas against an existing set of input/output mappings
 * and return the mappings that remain valid plus the discarded ones.
 *
 * `oldInputSchema` / `oldOutputSchema` may be `null` — in that case we skip
 * type-mismatch detection but still prune stale field IDs against the new
 * schema.
 */
export function computeMappingCleanup(
  oldInputSchema: MappingCleanupInputSchema | null,
  newInputSchema: MappingCleanupInputSchema,
  oldOutputSchema: MappingCleanupOutputSchema | null,
  newOutputSchema: MappingCleanupOutputSchema,
  inputMappings: ReadonlyArray<InputMapping>,
  outputMappings: ReadonlyArray<OutputMapping>
): MappingCleanupResult {
  const newInputIndex = buildTypeIndex(newInputSchema);
  const newOutputIndex = buildTypeIndex(newOutputSchema);
  const oldInputIndex = oldInputSchema ? buildTypeIndex(oldInputSchema) : null;
  const oldOutputIndex = oldOutputSchema
    ? buildTypeIndex(oldOutputSchema)
    : null;

  const cleanInputMappings: InputMapping[] = [];
  const removedInputs: RemovedInputMapping[] = [];
  for (const mapping of inputMappings) {
    const verdict = classifyInputMapping(mapping, oldInputIndex, newInputIndex);
    if (verdict === 'keep') {
      cleanInputMappings.push(mapping);
    } else {
      removedInputs.push({ mapping, reason: verdict });
    }
  }

  const cleanOutputMappings: OutputMapping[] = [];
  const removedOutputs: RemovedOutputMapping[] = [];
  for (const mapping of outputMappings) {
    const verdict = classifyOutputMapping(
      mapping,
      oldOutputIndex,
      newOutputIndex
    );
    if (verdict === 'keep') {
      cleanOutputMappings.push(mapping);
    } else {
      removedOutputs.push({ mapping, reason: verdict });
    }
  }

  return {
    cleanInputMappings,
    cleanOutputMappings,
    removed: {
      inputs: removedInputs,
      outputs: removedOutputs,
    },
  };
}

/** Convenience: collapse a WorkflowSchema.output Record into the flat shape. */
export function fromOutputRecord(
  output: Record<string, { type?: string }>
): MappingCleanupOutputSchema {
  return {
    fields: Object.entries(output).map(([id, def]) => ({
      id,
      type: def.type,
    })),
  };
}

/** Convenience: flatten a WorkflowSchema.input {required, optional} into fields. */
export function fromInputRequiredOptional(
  required: ReadonlyArray<{ id: string; type?: string }>,
  optional: ReadonlyArray<{ id: string; type?: string }>
): MappingCleanupInputSchema {
  return {
    fields: [...required, ...optional].map(f => ({ id: f.id, type: f.type })),
  };
}
